// Global
import React, { createContext, useContext } from 'react';
import clsx from 'clsx';

// Local
import { SideBySideContainer } from './SideBySideContainer';
import { PlaceholderWrapper } from 'components/helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { ComponentProps } from 'lib/component-props';
import { ComponentParams } from '@sitecore-content-sdk/nextjs';
import styles from './SideBySideContainer.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { Themes } from 'lib/themes';

type ColCount = '1' | '2' | undefined;
const SideBySideContext = createContext<ColCount>('2');
export const useSideBySideContext = () => useContext(SideBySideContext);

type SideBySideContainerDynamicProps = ComponentProps & {
  params?: ComponentParams & {
    leftPlaceholderId?: string;
    rightPlaceholderId?: string;
    numColumns?: ColCount;
  };
};

const SideBySideDynamic = (props: SideBySideContainerDynamicProps) => {
  const { rendering, params = {} } = props;
  const { getDictionaryValue } = useDictionary();
  const RenderingIdentifier = rendering?.params?.RenderingIdentifier;

  if (!rendering) {
    console.warn('Missing rendering prop in SideBySideDynamic');
    return <div id={RenderingIdentifier} />;
  }

  const colCount = (params.numColumns || '2') as ColCount;

  const { DynamicPlaceholderId } = params || {};

  const leftPhKey = DynamicPlaceholderId
    ? `side-by-side-left-container-${DynamicPlaceholderId}`
    : 'side-by-side-left-container';

  const rightPhKey = DynamicPlaceholderId
    ? `side-by-side-right-container-${DynamicPlaceholderId}`
    : 'side-by-side-right-container';

  return (
    <SideBySideContainer
      theme={props.params?.theme as Themes}
      RenderingIdentifier={RenderingIdentifier}
      colCount={colCount}
    >
      <SideBySideContext.Provider value={colCount}>
        <PlaceholderWrapper
          name={leftPhKey}
          rendering={rendering}
          render={(components) => (
            <div className={styles.side}>
              <div
                aria-label={
                  getDictionaryValue('Common.SideBySideContainer.LeftContentArea') ||
                  'Left content area'
                }
                className={clsx(styles.contentArea, styles.left)}
              >
                {components.map((comp, index) => (
                  <div
                    key={`left-${index}`}
                    className={clsx(styles.contentItem, styles[`col-${colCount}`])}
                    role="listitem"
                  >
                    {comp}
                  </div>
                ))}
              </div>
            </div>
          )}
        />

        <PlaceholderWrapper
          name={rightPhKey}
          rendering={rendering}
          render={(components) => (
            <div className={styles.side}>
              <div
                aria-label={
                  getDictionaryValue('Common.SideBySideContainer.RightContentArea') ||
                  'Right content area'
                }
                className={clsx(styles.contentArea, styles.right)}
              >
                {components.map((comp, index) => (
                  <div
                    key={`right-${index}`}
                    className={clsx(styles.contentItem, styles[`col-${colCount}`])}
                    role="listitem"
                  >
                    {comp}
                  </div>
                ))}
              </div>
            </div>
          )}
        />
      </SideBySideContext.Provider>
    </SideBySideContainer>
  );
};

export const Default = SideBySideDynamic;
